import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {RouterOutlet} from "@angular/router";
import {MsalBroadcastService, MsalService} from "@azure/msal-angular";
import {filter} from "rxjs";
import {AuthenticationResult, EventMessage, EventType, InteractionStatus} from "@azure/msal-browser";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AuthService} from "./auth/services/auth.service";

@Component({
    selector: 'app-root',
    imports: [
        RouterOutlet
    ],
    templateUrl: './app.html',
    styleUrls: ['./app.css']
})
export class App implements OnInit {
    private readonly msal = inject(MsalService);
    private readonly authService = inject(AuthService);
    private readonly broadcast = inject(MsalBroadcastService);
    private readonly destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        this.broadcast.msalSubject$
            .pipe(
                filter(
                    (m: EventMessage) => {
                        return m.eventType === EventType.LOGIN_SUCCESS ||
                            m.eventType === EventType.ACQUIRE_TOKEN_SUCCESS;
                    },
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((m) => {
                const payload = m.payload as AuthenticationResult;
                this.msal.instance.setActiveAccount(payload.account);
                this.logUser(m.eventType, payload.account);
            });

        // After every interaction settles (including page reload), make sure
        // there is an active account selected so MsalGuard and the interceptor
        // know which identity to use.
        this.broadcast.inProgress$
            .pipe(
                filter((status) => status === InteractionStatus.None),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                const accounts = this.msal.instance.getAllAccounts();
                let active = this.msal.instance.getActiveAccount();
                if (!active && accounts.length > 0) {
                    active = accounts[0];
                    this.msal.instance.setActiveAccount(active);
                }
                if (active) {
                    this.authService.getMicrosoftProfile();
                    this.logUser('interaction-settled', active);
                }
            });
    }

    private logUser(source: string, account: AuthenticationResult['account']): void {
        if (!account) return;
        const idTokenClaims = account.idTokenClaims as Record<string, unknown> | undefined;
        console.log('[Auth] user available', {
            source,
            username: account.username,
            name: account.name,
            tenantId: account.tenantId,
            localAccountId: account.localAccountId,
            homeAccountId: account.homeAccountId,
            roles: idTokenClaims?.['roles'],
            oid: idTokenClaims?.['oid'],
        });
    }
}
